import { AdjustmentStatuses } from 'constants/adjustmentStatuses';
import { NotificationTypes } from 'constants/notifications';

import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useNotify } from 'react-admin';
import { CardActionsButtons } from 'shared/mui/Card';
import { CardHeader } from 'shared/mui/Card/CardHeader/CardHeader';
import { ClickAwayListener } from 'shared/mui/ClickAwayListener/ClickAwayListener';
import { List } from 'shared/mui/List/List';
import { Popper } from 'shared/mui/Popper/Popper';
import { useGetNotifications } from 'shared/widgets/Notification/hooks/useGetNotifications';
import { useIntersectionChangeViewedStatus } from 'shared/widgets/Notification/hooks/useIntersectionChangeViewedStatus';
import {
  Notification,
  NotificationBodyOrderAdjustment,
  NotificationBodyReconciliation,
  NotificationBodyReport,
  NotificationBodyTask,
} from 'types/embedded/notification';
import { getId } from 'utils/getId';

import { NotificationPopperAlert } from './NotificationPopperAlert';
import {
  NotificationsPopperCard,
  NotificationsPopperCardContent,
} from './NotificationPopperCard.styled';
import { NotificationPopperListItemOrder } from './NotificationPopperListItem/NotificationPopperListItemOrder/NotificationPopperListItemOrder';
import { NotificationPopperListItemReconciliation } from './NotificationPopperListItem/NotificationPopperListItemReconciliation/NotificationPopperListItemReconciliation';
import { NotificationPopperListItemReport } from './NotificationPopperListItem/NotificationPopperListItemReport/NotificationPopperListItemReport';
import { NotificationPopperListItemTask } from './NotificationPopperListItem/NotificationPopperListItemTask/NotificationPopperListItemTask';
import { NotificationPopperMenuButton } from './NotificationPopperMenuButton';
import { NotificationPopperSkeleton } from './NotificationPopperSkeleton';

type NotificationsPopperProps = {
  anchorEl?: Element | null;
  onClose: () => void;
  refetchCounter: () => void;
};

export const NotificationsPopper = ({
  anchorEl,
  onClose,
  refetchCounter,
}: NotificationsPopperProps): JSX.Element => {
  const translate = useTypedTranslate();
  const {
    notifications,
    refetch: refetchNotifications,
    isLoading,
  } = useGetNotifications(Boolean(anchorEl));
  const notify = useNotify();
  const setListElement = useIntersectionChangeViewedStatus({
    notifications,
    onSuccess: () => {
      refetchCounter();
      refetchNotifications();
    },
  });

  const renderNotification = (notification: Notification): JSX.Element | null => {
    const notificationType = getId(notification.notificationType);

    if (notificationType === NotificationTypes.OrderAdjustment) {
      const message =
        'status' in notification.body && notification.body.status === AdjustmentStatuses.Completed
          ? translate('app.notificationPopper.orderAmountAdjustmentReady')
          : translate('app.notificationPopper.orderAmountAdjustmentStart');

      return (
        <NotificationPopperListItemOrder
          dataId={notification.id}
          key={notification.id}
          message={message}
          notification={notification as Notification<NotificationBodyOrderAdjustment>}
        />
      );
    }

    if (notificationType === NotificationTypes.CommissionRecalculationTasks) {
      return (
        <NotificationPopperListItemTask
          dataId={notification.id}
          key={notification.id}
          notification={notification as Notification<NotificationBodyTask>}
        />
      );
    }

    if (notificationType === NotificationTypes.Reconciliation) {
      return (
        <NotificationPopperListItemReconciliation
          dataId={notification.id}
          key={notification.id}
          notification={notification as Notification<NotificationBodyReconciliation>}
          onSuccess={() => {
            refetchCounter();
            refetchNotifications();
          }}
        />
      );
    }

    if (notificationType === NotificationTypes.Report) {
      return (
        <NotificationPopperListItemReport
          dataId={notification.id}
          key={notification.id}
          notification={notification as Notification<NotificationBodyReport>}
          onSuccess={() => {
            refetchCounter();
            refetchNotifications();
          }}
        />
      );
    }

    return null;
  };

  const listContent = isLoading ? (
    <NotificationPopperSkeleton />
  ) : notifications?.length ? (
    notifications.map(renderNotification)
  ) : notifications ? (
    <NotificationPopperAlert />
  ) : null;

  return (
    <Popper anchorEl={anchorEl} open={!!anchorEl} placement="bottom-end" sx={{ zIndex: 100 }}>
      <ClickAwayListener onClickAway={onClose}>
        <NotificationsPopperCard>
          <CardHeader
            appendActions={
              <CardActionsButtons
                rightActionsSlot={
                  notifications?.length ? (
                    <NotificationPopperMenuButton
                      notifications={notifications}
                      onSuccess={() => {
                        refetchCounter();
                        refetchNotifications();
                        notify(translate('app.notificationPopper.allChecked'), { type: 'success' });
                      }}
                    />
                  ) : undefined
                }
                sx={{ flex: 1, padding: 0 }}
              />
            }
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              padding: '16px 24px',
            }}
            title={translate('app.common.notifications')}
          />
          <NotificationsPopperCardContent>
            <List ref={setListElement} sx={{ padding: 0, maxHeight: 400, overflowY: 'auto' }}>
              {listContent}
            </List>
          </NotificationsPopperCardContent>
        </NotificationsPopperCard>
      </ClickAwayListener>
    </Popper>
  );
};
